/**
 * Calibrated glass-to-glass latency tracker.
 *
 * Given a correlated frame sample (host send time + guest decode time + the
 * host<->guest clock offset at that moment), produces an end-to-end latency in
 * host-clock terms: decodeWallMs + offsetMs - hostSendWallMs. The tracker keeps a
 * rolling window of calibrated samples so p50/p95 are stable enough to act on.
 *
 * This is the tracker the rVFC hint path fed into; the calibrated path replaces the
 * hints with datachannel-correlated measurements so Safari (no rVFC rtpTimestamp)
 * reports the same calibrated number as Chromium.
 *
 * It also owns the link's LATENCY FLOOR: the windowed minimum, which is propagation plus
 * the fixed pipeline (encode, jitter-buffer target, decode, vsync) — the part of the latency
 * no encoder setting can change. Everything above it (`excessMs`) is queueing or a decoder
 * falling behind, and is the only part a bitrate/resolution/fps cut can act on. The split is
 * what lets the latency budget act on congestion without punishing distance: a guest far
 * from the host has a high floor, not a faulty link.
 */

import { RTT_FLOOR_MIN_SAMPLES } from './rttFloorTracker';
import type { FrameTimingSource, LatencyFreshnessSnapshot, MeasurementFreshness } from './streamRecovery/contracts';
import { FRAME_EVIDENCE_STALE_MS } from './streamRecovery/policy';

/**
 * Below this many samples the floor is untrusted and no excess may be reported. Shares the
 * RTT floor's rule for the same reason: a floor built from one or two readings treats a
 * first-ever spike as its own baseline.
 */
export const CALIBRATED_LATENCY_FLOOR_MIN_SAMPLES = RTT_FLOOR_MIN_SAMPLES;

export type CalibratedLatencySample = {
  observationSequence?: number;
  observationAgeMs?: number;
  pathEpoch?: number;
  timingSource?: FrameTimingSource;
  /** RTP timestamp of the frame being measured. */
  rtpTimestamp: number;
  /** Host local wall time (ms) when the frame was encoded/sent. */
  hostSendWallMs: number;
  /** Guest local wall time (ms) when the frame was decoded. */
  guestDecodeWallMs: number;
  /** Host->guest clock offset (ms) at correlation time. */
  offsetMs: number;
  /** End-to-end latency in host clock terms, ms. */
  calibratedMs: number;
};

export class CalibratedLatencyTracker {
  private readonly windowMs: number;
  private readonly nowMs: () => number;
  private samples: { atMs: number; latencyMs: number; key: string }[] = [];
  private pathEpoch = 0;
  private sequence: number | undefined;
  private clockAtMs: number | undefined;
  private latest: { acceptedAt: number; ageMs: number; latencyMs: number; timingSource: FrameTimingSource; key: string } | undefined;

  constructor(options: { windowMs?: number; nowMs?: () => number } = {}) {
    this.windowMs = options.windowMs ?? 30_000;
    this.nowMs = options.nowMs ?? (() => performance.now());
  }

  /** Feed one calibrated sample. Out-of-window samples are dropped. */
  observe(sample: CalibratedLatencySample): boolean {
    if (
      !Number.isFinite(sample.calibratedMs) ||
      !Number.isFinite(sample.rtpTimestamp) || sample.rtpTimestamp < 0 ||
      sample.calibratedMs < 0 ||
      !Number.isFinite(sample.hostSendWallMs) ||
      !Number.isFinite(sample.guestDecodeWallMs) ||
      !Number.isFinite(sample.offsetMs)
    ) {
      return false;
    }
    const epoch = sample.pathEpoch ?? 0;
    if (!Number.isSafeInteger(epoch) || epoch < this.pathEpoch) return false;
    if (epoch > this.pathEpoch) this.resetForPath(epoch);
    const sequence = sample.observationSequence;
    if (sequence !== undefined && (!Number.isSafeInteger(sequence) || sequence < 0 ||
        (this.sequence !== undefined && sequence <= this.sequence))) return false;
    const ageMs = sample.observationAgeMs ?? 0;
    if (!Number.isFinite(ageMs) || ageMs < 0) return false;
    const key = `${sample.rtpTimestamp}:${sample.guestDecodeWallMs}`;
    const atMs = this.readClock();
    if (!Number.isFinite(atMs)) return false;
    this.prune(atMs);
    if (this.latest?.key === key || this.samples.some((entry) => entry.key === key)) return false;
    this.sequence = sequence ?? this.sequence;
    this.latest = { acceptedAt: atMs, ageMs, latencyMs: sample.calibratedMs, timingSource: sample.timingSource ?? 'callback-time', key };
    this.samples.push({ atMs: atMs - ageMs, latencyMs: sample.calibratedMs, key });
    this.prune();
    return true;
  }

  /** Latest calibrated latency in ms; null before any sample exists. */
  lastMs(): number | null {
    return this.freshness().state === 'fresh' ? this.latest?.latencyMs ?? null : null;
  }

  snapshot(): LatencyFreshnessSnapshot {
    return { freshness: this.freshness(), timingSource: this.latest?.timingSource ?? 'unavailable',
      lastMs: this.lastMs(), lastObservedMs: this.latest?.latencyMs ?? null,
      p50Ms: this.p50Ms(), p95Ms: this.p95Ms(), floorMs: this.floorMs() };
  }

  resetForPath(pathEpoch: number): void {
    if (!Number.isSafeInteger(pathEpoch) || pathEpoch < this.pathEpoch) return;
    this.reset();
    this.pathEpoch = pathEpoch;
  }

  p50Ms(): number | null {
    return this.percentile(0.5);
  }

  p95Ms(): number | null {
    return this.percentile(0.95);
  }

  /**
   * This link's latency floor — the window minimum — or null while untrusted. Follows the
   * window: an elevation that outlives it becomes the floor, which is correct by construction
   * (whatever a cut could not drain in that long is not excess).
   */
  floorMs(): number | null {
    this.prune();
    if (this.samples.length < CALIBRATED_LATENCY_FLOOR_MIN_SAMPLES) return null;
    return this.samples.reduce(
      (min, s) => (s.latencyMs < min ? s.latencyMs : min),
      Number.POSITIVE_INFINITY,
    );
  }

  /** The latest reading above the floor — the fixable part — or null while the floor is untrusted. */
  excessMs(): number | null {
    const last = this.lastMs();
    const floor = this.floorMs();
    if (last === null || floor === null) return null;
    return Math.max(0, last - floor);
  }

  sampleCount(): number {
    this.prune();
    return this.samples.length;
  }

  reset(): void {
    this.samples = [];
    this.latest = undefined;
    this.sequence = undefined;
    this.pathEpoch = 0;
    this.clockAtMs = undefined;
  }

  private freshness(): MeasurementFreshness {
    const now = this.readClock();
    if (!this.latest) return { state: 'unavailable' };
    const ageMs = this.latest.ageMs + now - this.latest.acceptedAt;
    return { state: ageMs < FRAME_EVIDENCE_STALE_MS ? 'fresh' : 'stale', ageMs,
      ...(this.sequence !== undefined ? { sequence: this.sequence } : {}) };
  }

  private prune(now = this.readClock()): void {
    this.samples = this.samples.filter((sample) => now >= sample.atMs && now - sample.atMs < this.windowMs);
  }

  private readClock(): number {
    const now = this.nowMs();
    if (!Number.isFinite(now) || (this.clockAtMs !== undefined && now < this.clockAtMs)) {
      this.samples = [];
      this.latest = undefined;
      this.sequence = undefined;
    }
    this.clockAtMs = Number.isFinite(now) ? now : undefined;
    return now;
  }

  private percentile(p: number): number | null {
    this.prune();
    if (this.samples.length === 0) return null;
    const sorted = this.samples.map((s) => s.latencyMs).sort((a, b) => a - b);
    return sorted[Math.floor((sorted.length - 1) * p)];
  }
}
